/**
 * Generic Brand Landing Page
 *
 * Fallback landing page for brands without custom implementations.
 * Uses BrandContext to dynamically display brand-specific colors and info.
 */

import React, { useState } from 'react';
import { useBrand } from '../../contexts/BrandContext';
import { API_ENDPOINTS } from '../../services/api/endpoints';

const GenericBrandLanding: React.FC = () => {
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const { brand, brandCode, primaryColor, isLoading } = useBrand();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  const brandName = brand?.Brand_Name || brandCode.charAt(0).toUpperCase() + brandCode.slice(1);
  const tagline = brand?.Brand_Name ? `AI-Powered ${brandName}` : 'Coming Soon';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section
        className="text-white py-20"
        style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)` }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-block px-4 py-1 bg-white/20 rounded-full text-sm font-medium mb-4">
              Equilateral AI
            </span>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {brandName}
            </h1>
            <p className="text-xl md:text-2xl opacity-90 mb-8 max-w-3xl mx-auto">
              {tagline}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                className="bg-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
                style={{ color: primaryColor }}
                onClick={() => setShowWaitlist(true)}
              >
                Join Waitlist
              </button>
              <a
                href="https://equilateral.ai"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/10 transition-colors"
              >
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Coming Soon */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-2xl mx-auto">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <svg
                className="w-10 h-10"
                style={{ color: primaryColor }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
                />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Under Development
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              {brandName} is part of the Equilateral AI family of products.
              We're working on something special. Join the waitlist to be notified when we launch.
            </p>
            {brand?.Enabled_Features && brand.Enabled_Features.length > 0 && (
              <div className="flex flex-wrap justify-center gap-2">
                {brand.Enabled_Features.slice(0, 5).map((feature) => (
                  <span
                    key={feature}
                    className="px-3 py-1 rounded-full text-sm font-medium"
                    style={{
                      backgroundColor: `${primaryColor}15`,
                      color: primaryColor,
                    }}
                  >
                    {feature.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-16 text-white"
        style={{ backgroundColor: primaryColor }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Be the First to Know
          </h2>
          <p className="opacity-90 mb-8 max-w-2xl mx-auto">
            Join our waitlist and get early access when {brandName} launches.
          </p>
          <button
            className="bg-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
            style={{ color: primaryColor }}
            onClick={() => setShowWaitlist(true)}
          >
            Join Waitlist
          </button>
        </div>
      </section>

      {/* Waitlist Modal */}
      {showWaitlist && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Join the {brandName} Waitlist
            </h3>
            <p className="text-gray-600 mb-6">
              Be notified when {brandName} launches.
            </p>
            <form
              className="space-y-4"
              onSubmit={async (e) => {
                e.preventDefault();
                if (!waitlistEmail || waitlistSubmitting) return;
                setWaitlistSubmitting(true);
                try {
                  const response = await fetch(API_ENDPOINTS.BRANDS.WAITLIST, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      email: waitlistEmail,
                      brand: brandCode || 'unknown',
                    }),
                  });
                  const data = await response.json();
                  if (data.success) {
                    setShowWaitlist(false);
                    setWaitlistEmail('');
                    alert('You have been added to the waitlist!');
                  } else {
                    alert(data.message || 'Something went wrong. Please try again.');
                  }
                } catch (err) {
                  console.error('[GenericBrandLanding] Waitlist signup error:', err);
                  alert('Something went wrong. Please try again.');
                } finally {
                  setWaitlistSubmitting(false);
                }
              }}
            >
              <input
                type="email"
                placeholder="Email Address"
                required
                value={waitlistEmail}
                onChange={(e) => setWaitlistEmail(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': primaryColor } as React.CSSProperties}
              />
              <div className="flex gap-4">
                <button
                  type="button"
                  className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  onClick={() => setShowWaitlist(false)}
                  disabled={waitlistSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 text-white rounded-lg hover:opacity-90"
                  style={{ backgroundColor: primaryColor }}
                  disabled={waitlistSubmitting}
                >
                  {waitlistSubmitting ? 'Joining...' : 'Join Waitlist'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenericBrandLanding;
